<!-- src\components\ui\SpollerBlock.vue -->
<template>
	<div
		ref="spollersRef"
		class="spollers"
		:data-one-spoller="oneSpoller || null"
		:data-spollers="dataSpollers"
		:data-spollers-close="closeOnOutside ? true : null"
		:data-spollers-speed="speed"
	>
		<slot />
	</div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { _slideUp, _slideDown } from '@/utils/SlideAnimation'

const props = defineProps({
	dataSpollers: String,
	oneSpoller: Boolean,
	closeOnOutside: Boolean,
	speed: {
		type: Number,
		default: 500,
	},
})

const spollersRef = ref(null)
let mediaQueryList = null
let mediaQueryListener = null
let spollerButtons = []

function initSpollerBody(spollersBlock, hideBody = true) {
	const titles = spollersBlock.querySelectorAll('[data-spoller]')
	titles.forEach((title) => {
		const body = title.nextElementSibling
		if (!body) return
		if (hideBody) {
			if (!title.classList.contains('_spoller-active')) {
				body.hidden = true
			}
			title.removeAttribute('tabindex')
		} else {
			body.hidden = false
			title.setAttribute('tabindex', '-1')
		}
	})
	if (window.innerWidth <= 991.98) {
		const firstSpoller = spollersBlock.querySelector('[data-spoller]')
		if (firstSpoller && !firstSpoller.classList.contains('_spoller-active')) {
			firstSpoller.classList.add('_spoller-active')
			_slideDown(
				firstSpoller.nextElementSibling,
				parseInt(spollersBlock.dataset.spollersSpeed) || 500,
			)
		}
	}
}

function hideSpollersBody(spollersBlock) {
	const activeTitle = spollersBlock.querySelector('[data-spoller]._spoller-active')
	if (!activeTitle) return
	const body = activeTitle.nextElementSibling
	activeTitle.classList.remove('_spoller-active')
	_slideUp(body, parseInt(spollersBlock.dataset.spollersSpeed) || 500)
}

function toggleSpoller(el) {
	const spollersBlock = el.closest('[data-spollers]')
	if (!spollersBlock) return

	const oneSpoller = spollersBlock.hasAttribute('data-one-spoller')

	const spollerSpeed = parseInt(spollersBlock.dataset.spollersSpeed) || 500

	if (!spollersBlock.querySelectorAll('._slide').length) {
		if (oneSpoller && !el.classList.contains('_spoller-active')) {
			hideSpollersBody(spollersBlock)
		}

		el.classList.toggle('_spoller-active')

		const body = el.nextElementSibling
		if (!body) return

		if (el.classList.contains('_spoller-active')) {
			_slideDown(body, spollerSpeed)
		} else {
			_slideUp(body, spollerSpeed)
		}
	}
}

function openSpollerStrict(el) {
	const spollersBlock = el.closest('[data-spollers]')
	if (!spollersBlock) {
		console.log('[openSpollerStrict] spollersBlock не найден')
		return
	}

	const spollerSpeed = parseInt(spollersBlock.dataset.spollersSpeed) || 500
	console.log('[openSpollerStrict] Спойлер с длительностью анимации:', spollerSpeed)

	// Закрыть все активные спойлеры, кроме текущего
	const activeTitles = spollersBlock.querySelectorAll('[data-spoller]._spoller-active')
	console.log('[openSpollerStrict] Найдено активных спойлеров:', activeTitles.length)

	activeTitles.forEach((activeTitle) => {
		if (activeTitle !== el) {
			console.log('[openSpollerStrict] Закрываем спойлер:', activeTitle.textContent.trim())
			activeTitle.classList.remove('_spoller-active')
			const body = activeTitle.nextElementSibling
			if (body) {
				_slideUp(body, spollerSpeed)
				setTimeout(() => {
					body.hidden = true
					console.log(
						'[openSpollerStrict] Скрываем тело спойлера:',
						activeTitle.textContent.trim(),
					)
				}, spollerSpeed)
			}
		}
	})

	// Открыть нужный спойлер, если он ещё не открыт
	if (!el.classList.contains('_spoller-active')) {
		console.log('[openSpollerStrict] Открываем спойлер:', el.textContent.trim())
		el.classList.add('_spoller-active')
		const body = el.nextElementSibling
		if (body) {
			body.hidden = false
			_slideDown(body, spollerSpeed)
		}
		console.log(
			'[openSpollerStrict] Класс _spoller-active после открытия?',
			el.classList.contains('_spoller-active'),
		)
	} else {
		console.log('[openSpollerStrict] Спойлер уже открыт:', el.textContent.trim())
	}
}

// Обработчик клика для навешивания на каждый спойлер
function spollerClickHandler(e) {
	toggleSpoller(e.currentTarget)
}

function onClickOutside(e) {
	if (!spollersRef.value) return
	if (e.target.closest('[data-spollers]') !== spollersRef.value) {
		const opened = spollersRef.value.querySelectorAll('[data-spoller]._spoller-active')
		opened.forEach((title) => {
			title.classList.remove('_spoller-active')
			const body = title.nextElementSibling
			if (body) _slideUp(body, parseInt(spollersRef.value.dataset.spollersSpeed) || 500)
		})
	}
}

function parseMediaQuery(data) {
	if (!data) return null
	const [value, type] = data.split(',').map((s) => s.trim())
	if (!value || !type) return null
	const pxValue = parseFloat(value)
	if (isNaN(pxValue)) return null
	if (type === 'max') return window.matchMedia(`(max-width: ${pxValue}px)`)
	if (type === 'min') return window.matchMedia(`(min-width: ${pxValue}px)`)
	return null
}

function addSpollerListeners() {
	if (!spollersRef.value) return
	spollerButtons = spollersRef.value.querySelectorAll('[data-spoller]')
	spollerButtons.forEach((btn) => {
		btn.addEventListener('click', spollerClickHandler)
	})
}

function removeSpollerListeners() {
	spollerButtons.forEach((btn) => {
		btn.removeEventListener('click', spollerClickHandler)
	})
	spollerButtons = []
}

function initSpollers() {
	if (!spollersRef.value) return

	const spollersBlock = spollersRef.value
	const dataSpollersAttr = spollersBlock.getAttribute('data-spollers')
	mediaQueryList = parseMediaQuery(dataSpollersAttr)

	function toggleSpollers(match = true) {
		if (match) {
			spollersBlock.classList.add('_spoller-init')
			initSpollerBody(spollersBlock)
			addSpollerListeners()
			if (props.closeOnOutside) {
				document.addEventListener('click', onClickOutside)
			}
		} else {
			spollersBlock.classList.remove('_spoller-init')
			initSpollerBody(spollersBlock, false)
			removeSpollerListeners()
			if (props.closeOnOutside) {
				document.removeEventListener('click', onClickOutside)
			}
		}
	}

	if (mediaQueryList) {
		toggleSpollers(mediaQueryList.matches)
		mediaQueryListener = (e) => toggleSpollers(e.matches)
		mediaQueryList.addEventListener('change', mediaQueryListener)
	} else {
		toggleSpollers(true)
	}
}

// onMounted(() => {
// 	initSpollers()
// })

// watch(
// 	() => spollersRef.value?.children.length,
// 	async () => {
// 		await nextTick()
// 		if (spollersRef.value) {
// 			removeSpollerListeners()
// 			initSpollers()
// 		}
// 	},
// )

onBeforeUnmount(() => {
	if (mediaQueryList && mediaQueryListener) {
		mediaQueryList.removeEventListener('change', mediaQueryListener)
	}
	if (props.closeOnOutside) {
		document.removeEventListener('click', onClickOutside)
	}
	removeSpollerListeners()
})

defineExpose({
	initSpollers,
	toggleSpoller,
	openSpollerStrict,
})
</script>
